// File: src/pages/reveal/RevealInMatch.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import RevealPortalNavBar from "../../components/Reveal/RevealPortalNavBar";
import {
  getRevealSocket,
  startRevealAccessWatch,
  stopRevealAccessWatch,
  startLocationHeartbeat,
  stopLocationHeartbeat,
} from "../../utils/revealSocket";
import { enforceRevealState } from "../../utils/revealEnforcer";

/* ---------------- DEBUG LOGGER ---------------- */
const DBG = true;
const TAG = "[Reveal/InMatch]";
const L = (...args) => { if (DBG) console.log(TAG, ...args); };

/* -------------------------------- helpers -------------------------------- */
function authHeaders() {
  const token =
    localStorage.getItem("accessToken") ||
    JSON.parse(localStorage.getItem("tribe_auth") || "{}")?.token ||
    "";
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
}
function getMyId() {
  const auth = JSON.parse(localStorage.getItem("tribe_auth") || "{}");
  return (
    auth?.uid ||
    auth?.userId ||
    auth?.user?._id ||
    localStorage.getItem("userId") ||
    ""
  );
}

export default function RevealInMatch() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useMemo(() => getRevealSocket(), []);
  const api = process.env.REACT_APP_API_URL;

  /* ───────────────── HARD PRE-FLIGHT (no render until server says inmatch) ───────────────── */
  const [gateReady, setGateReady] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
          authHeaders()
        );
        const route = String(data?.route || "portal");
        const endMs = Number.isFinite(data?.endMs) ? Number(data.endMs) : null;

        if (route !== "inmatch" || !endMs || endMs <= Date.now() + 500) {
          // Clear any stale local “stickiness” and bounce before mounting UI
          try { sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`); } catch {}
          const want = route === "partner" ? "partner" : "portal";
          navigate(`/reveal/${eventId}/${want}`, {
            replace: true,
            state: {
              stage: Number.isFinite(data?.stageIndex) ? data.stageIndex : null,
              matchId: data?.matchId || null,
              stageEndsAt: null,
              fromEnforcer: true,
            },
          });
          return;
        }
        if (alive) setGateReady(true);
      } catch {
        navigate(`/reveal/${eventId}/portal`, { replace: true });
      }
    })();
    return () => { alive = false; };
  }, [api, eventId, navigate]);

  useEffect(() => {
    function onRouteIntent(e) {
      const p = e.detail || {};
      if (!p?.route) return;
      console.log('[Client] 🔄 route-intent →', p);
      navigate(`/reveal/${eventId}/${p.route}`, {
        replace: true,
        state: { stage: p.stageIndex ?? null, fromSocket: true },
      });
    }
    window.addEventListener('reveal:route-intent', onRouteIntent);
    return () => window.removeEventListener('reveal:route-intent', onRouteIntent);
  }, [eventId, navigate]);
  

  useEffect(() => {
    const s = getRevealSocket();
    const enter = () => { try { s.emit('revealPortal:enter', { eventId }); } catch {} };
    enter();
    s.on('connect', enter);
    return () => s.off('connect', enter);
  }, [eventId]);


  /* ───────── Enforce server route after gate opens ───────── */
  useEffect(() => {
    if (!gateReady) return;
    enforceRevealState({ api, eventId, pageKind: "inmatch", navigate });
  }, [api, eventId, navigate, gateReady]);

  /* ---------------- nav state ---------------- */
const navState = location.state || {};
const [iceQ, setIceQ] = useState(
  typeof navState.icebreakerQuestion === "string" ? navState.icebreakerQuestion : ""
);
const hasTwo = !!navState.hasTwo; // ← add this line

  // Use nav end only if provided; do NOT seed a fake 5:00
  const rawEnds = Number(navState.stageEndsAt || 0);
  const stageEndsAtFromNav =
    Number.isFinite(rawEnds) && rawEnds > Date.now() + 2000 ? rawEnds : null;

  const [leftSec, setLeftSec] = useState(-1);

  // Track a real matchId in state to trigger fetches
  const [matchIdState, setMatchIdState] = useState(
    location.state?.matchId || null
  );

  // server-truth refs
  const endEpochRef = useRef(null); // ms
  const matchIdRef = useRef(matchIdState || null); // string
  const hardTimeoutRef = useRef(null); // setTimeout id
  const restEndedOnceRef = useRef(false); // ensure we only hit REST once

  // timer machinery
  const tickIdRef = useRef(null);
  const stuckIdRef = useRef(null);
  const lastShownRef = useRef(null);
  const armedRef = useRef(false);
  const navigatedRef = useRef(false);

  /* ---------------- Access / presence ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    L("mount", { eventId, navState });
    startLocationHeartbeat();
    startRevealAccessWatch(eventId, {
      onDenied: (data) => {
        L("ACCESS DENIED watchdog → portal", data);
        navigate(`/events/${eventId}`, { replace: true });
      },
      intervalMs: 15000,
    });
    return () => {
      stopRevealAccessWatch(eventId);
      stopLocationHeartbeat();
      L("unmount");
    };
  }, [gateReady, eventId, navigate, navState]);

  /* ---------------- join user room for unicast ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    const uid = getMyId();
    if (!uid) return;
    try {
      socket.emit("joinUserRoom", String(uid));
      L("joinUserRoom", uid);
    } catch (e) {
      L("joinUserRoom error", e?.message);
    }
  }, [gateReady, socket]);

  /* ---------------- timer helpers (visual) ---------------- */
  function snap() {
    if (!endEpochRef.current) return;
    const now = Date.now();
    const left = Math.max(0, Math.floor((endEpochRef.current - now) / 1000));
    setLeftSec(left);
  }
  function ensureIntervalRunning() {
    if (!tickIdRef.current) {
      tickIdRef.current = setInterval(() => {
        if (!endEpochRef.current) return;
        const now = Date.now();
        const left = Math.max(0, Math.floor((endEpochRef.current - now) / 1000));
        setLeftSec((prev) => (prev !== left ? left : prev));
        if (!armedRef.current && left > 0) armedRef.current = true;
      }, 1000);
    }
    if (!stuckIdRef.current) {
      stuckIdRef.current = setInterval(() => {
        const key = leftSec;
        if (
          lastShownRef.current === key &&
          endEpochRef.current &&
          endEpochRef.current > Date.now() + 1500
        ) {
          L("watchdog → kick snap");
          snap();
        }
        lastShownRef.current = key;
      }, 2000);
    }
  }
  function clearTimers() {
    if (tickIdRef.current) {
      clearInterval(tickIdRef.current);
      tickIdRef.current = null;
    }
    if (stuckIdRef.current) {
      clearInterval(stuckIdRef.current);
      stuckIdRef.current = null;
    }
    if (hardTimeoutRef.current) {
      clearTimeout(hardTimeoutRef.current);
      hardTimeoutRef.current = null;
    }
  }
  function setEndTarget(msEpoch) {
    if (!Number.isFinite(msEpoch)) return;
    const changedEnough =
      !endEpochRef.current || Math.abs(endEpochRef.current - msEpoch) > 500;
    endEpochRef.current = msEpoch;
    try {
      // Make InMatch “sticky” until this absolute time
      sessionStorage.setItem(`reveal_inmatch_until:${eventId}`, String(msEpoch));
    } catch {}
    armedRef.current = false;
    snap();
    ensureIntervalRunning();
    if (changedEnough) armHardTimeout(msEpoch);
    L("setEndTarget", {
      endMs: msEpoch,
      in: Math.round((msEpoch - Date.now()) / 1000) + "s",
    });
  }

  /* ---------------- authoritative ABS timeout ---------------- */
  function armHardTimeout(endMs) {
    if (hardTimeoutRef.current) clearTimeout(hardTimeoutRef.current);
    const delay = Math.max(0, endMs - Date.now() + 1500); // 1.5s grace
    hardTimeoutRef.current = setTimeout(async () => {
      if (navigatedRef.current) return;
      L("ABS timeout reached → forcing end");
      // best-effort ask server to end, then navigate
      const mid = matchIdRef.current;
      if (mid) {
        try {
          await axios.post(`${api}/matches/${mid}/end-session`, {}, authHeaders());
          restEndedOnceRef.current = true;
        } catch {}
      }
      safeNavigateAfterEnd();
    }, delay);
  }

  /* ---------------- seed from nav only when present ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    if (stageEndsAtFromNav) setEndTarget(stageEndsAtFromNav);
    return () => clearTimers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateReady, stageEndsAtFromNav]);

  /* ---------------- authoritative state: route + endMs + matchId ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    (async () => {
      try {
        const { data } = await axios.get(
          `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
          authHeaders()
        );
        if (data?.route === "inmatch") {
          if (data?.matchId) {
            matchIdRef.current = String(data.matchId);
            if (String(matchIdState) !== String(data.matchId)) {
              setMatchIdState(String(data.matchId));
            }
          }
          if (Number.isFinite(data?.endMs)) {
            setEndTarget(Number(data.endMs));
          }
        }
      } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateReady, api, eventId]);

  /* ---------------- fetch match doc for precise endTime ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    const mid = matchIdState || location.state?.matchId || null;
    if (!mid) return;
    (async () => {
      try {
        const { data } = await axios.get(`${api}/matches/${mid}`, authHeaders());
        const doc = data?.match || data || null;
        const q = typeof doc?.icebreakerQuestion === "string" ? doc.icebreakerQuestion : "";
     if (q) setIceQ(q); // ← add
        const ms = doc?.endTime ? new Date(doc.endTime).getTime() : null;
        if (Number.isFinite(ms)) setEndTarget(ms);
      } catch {}
    })();
  }, [gateReady, api, matchIdState, location.state?.matchId]);

  /* ---------------- resolve matchId; DO NOT set end from roundEndAt ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    let stop = false;
    (async () => {
      try {
        const { data, status } = await axios.get(
          `${api}/reveal-mode/${eventId}/match?t=${Date.now()}`,
          { ...authHeaders(), validateStatus: (s) => [200, 202, 204, 410].includes(s) }
        );
        L("GET /reveal-mode/:id/match =>", status, data?.state, {
          cnt: data?.matches?.length,
        });
        if (
          status === 200 &&
          data?.state === "match" &&
          Array.isArray(data.matches) &&
          data.matches.length
        ) {
          const first = data.matches[0];
          const id = String(first.matchId || "");
          if (id) {
            matchIdRef.current = id;
            if (String(matchIdState) !== id) setMatchIdState(id);
          }
          // ⛔️ don't set endTarget from roundEndAt here; we use /matches/:id endTime
        }
        if (status === 410) {
          // /match returns 410 when the REVEAL window is over — but we might still be
          // in a LIVE InMatch (5:00). If our local inmatch end timer says we're live,
          // ignore this and stay put.
          if (endEpochRef.current && endEpochRef.current > Date.now() + 1500) {
            L("ignore 410 from /match during live InMatch (local timer says live)");
            return;
          }
          // Double-check server truth: if /state still reports inmatch with time left, re-seed and stay.
          try {
            const { data: st } = await axios.get(
              `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
              authHeaders()
            );
            const endMs = Number.isFinite(st?.endMs) ? Number(st.endMs) : null;
            if (st?.route === "inmatch" && endMs && endMs > Date.now() + 1500) {
              setEndTarget(endMs);
              L("reseed inmatch end from /state after 410", { endMs });
              return;
            }
          } catch (e2) {
            L("state recheck after 410 error", e2?.message);
          }
          // Only navigate out if both local + /state do NOT confirm a live inmatch
          safeNavigateAfterEnd(true);
        }
      } catch (e) {
        const s = e?.response?.status;
        L("ensure matchId error", s, e?.message);
        // Mirror the same 410 handling in the catch branch
        if (s === 410) {
          if (endEpochRef.current && endEpochRef.current > Date.now() + 1500) {
            L("ignore 410 (catch) during live InMatch (local timer says live)");
            return;
          }
          try {
            const { data: st } = await axios.get(
              `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
              authHeaders()
            );
            const endMs = Number.isFinite(st?.endMs) ? Number(st.endMs) : null;
            if (st?.route === "inmatch" && endMs && endMs > Date.now() + 1500) {
              setEndTarget(endMs);
              L("reseed inmatch end from /state after 410 (catch)", { endMs });
              return;
            }
          } catch (e2) {
            L("state recheck after 410 (catch) error", e2?.message);
          }
          safeNavigateAfterEnd(true);
        }
      }
      if (!stop) return;
    })();
    return () => { stop = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateReady, api, eventId]);

  function safeNavigateAfterEnd() {
    if (navigatedRef.current) return;
    navigatedRef.current = true;
  
    clearTimers();
    try { sessionStorage.removeItem(`reveal_inmatch_until:${eventId}`); } catch {}
  
    // Best-effort REST bump (guarded elsewhere in your code too)
    const mid = matchIdRef.current;
    if (mid && !restEndedOnceRef.current) {
      restEndedOnceRef.current = true;
      axios.post(`${api}/matches/${mid}/end-session`, {}, authHeaders()).catch(() => {});
    }
  
    (async () => {
      try {
        const res = await axios.get(
          `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
          authHeaders()
        );
        const route = String(res?.data?.route || '');
        const stage = Number.isInteger(res?.data?.stageIndex) ? res.data.stageIndex : 1;
        if (route === 'partner') {
          navigate(`/reveal/${eventId}/partner`, { replace: true, state: { stage } });
          return;
        }
      } catch (_) { /* fall through */ }
  
      // Default: portal
      navigate(`/reveal/${eventId}/portal`, { replace: true });
    })();
  }
  

  useEffect(() => {
    if (!gateReady) return;
    if (!socket) return;

    function onStageUnlocked({ eventId: eid }) {
      if (String(eid) !== String(eventId)) return;
      L("reveal:stage-unlocked → backend bumped session end");
      safeNavigateAfterEnd();
    }
    socket.on("reveal:stage-unlocked", onStageUnlocked);
    return () => socket.off("reveal:stage-unlocked", onStageUnlocked);
  }, [gateReady, socket, eventId]);

  useEffect(() => {
    if (!gateReady) return;
    if (!socket) return;

    const onMatchUpdated = (m) => {
      try {
        const myId = getMyId();
        const mine = (m?.participants || []).some(
          (p) => String(p?._id || p) === String(myId)
        );
        const sameEvent =
          String(m?.eventId?._id || m?.eventId || "") === String(eventId);
        if (!mine || !sameEvent) return;

        // capture matchId once
        if (!matchIdRef.current && m?._id) matchIdRef.current = String(m._id);
        if (m?._id) {
          const id = String(m._id);
          if (String(matchIdState) !== id) setMatchIdState(id);
        }
   // NEW: keep the question synced from the server
   if (m && typeof m.icebreakerQuestion === "string" && m._id && String(m._id) === String(matchIdRef.current)) {
    setIceQ(m.icebreakerQuestion);
  }
        // resync endTime from server (authoritative)
        if (m?.endTime) {
          const ms = new Date(m.endTime).getTime();
          if (Number.isFinite(ms)) setEndTarget(ms);
        }

        // server ended / completed → bounce immediately
        const status = String(m?.status || "").toLowerCase();
        if (status === "completed") {
          L("matchUpdated => completed (fallback REST end) → navigate");
          const mid = String(m?._id || matchIdRef.current || "");
          if (mid && !restEndedOnceRef.current) {
            restEndedOnceRef.current = true;
            axios.post(`${api}/matches/${mid}/end-session`, {}, authHeaders())
              .then(() => L("POST /matches/:id/end-session (fallback)"))
              .catch(() => {}); // fire-and-forget
          }
          safeNavigateAfterEnd();
        }
      } catch {}
    };

    const onEnded = () => {
      L("socket ended event → navigate");
      safeNavigateAfterEnd();
    };

    const onEpoch = ({ eventId: eid }) => {
      if (String(eid) !== String(eventId)) return;
      L("reveal:epoch → navigate portal");
      navigatedRef.current = true;
      clearTimers();
      navigate(`/reveal/${eventId}/portal`, { replace: true });
    };

    const onReconnect = () => {
      L("reconnect → snap + rearm");
      snap();
      ensureIntervalRunning();
      // Prefer authoritative /state on reconnect
      (async () => {
        try {
          const { data } = await axios.get(
            `${api}/reveal-mode/${eventId}/state?t=${Date.now()}`,
            authHeaders()
          );
          if (data?.route === "inmatch") {
            if (data?.matchId) {
              matchIdRef.current = String(data.matchId);
              if (String(matchIdState) !== String(data.matchId)) {
                setMatchIdState(String(data.matchId));
              }
            }
            if (Number.isFinite(data?.endMs)) {
              setEndTarget(Number(data.endMs));
            }
          } else if (data?.route) {
            // let the guard redirect on next tick
          }
        } catch {}
      })();
    };

    socket.on("matchUpdated", onMatchUpdated);
    socket.on("match_ended", onEnded);
    socket.on("reveal:sessionEnded", onEnded);
    socket.on("reveal:epoch", onEpoch);
    socket.on("connect", onReconnect);

    return () => {
      socket.off("matchUpdated", onMatchUpdated);
      socket.off("match_ended", onEnded);
      socket.off("reveal:sessionEnded", onEnded);
      socket.off("reveal:epoch", onEpoch);
      socket.off("connect", onReconnect);
    };
  }, [gateReady, socket, eventId, navigate, hasTwo, api, matchIdState]);

  /* ---------------- visibility/focus drift correction ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    const fixDrift = () => {
      snap();
      L("visibility/focus → snap");
    };
    const onVisible = () =>
      document.visibilityState === "visible" && fixDrift();
    const onFocus = () => fixDrift();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [gateReady]);

  /* ---------------- TIMEOUT (visual) → ask server then navigate ---------------- */
  useEffect(() => {
    if (!gateReady) return;
    if (navigatedRef.current) return;
    if (!armedRef.current) return;
    if (!endEpochRef.current) return;

    if (leftSec > 0) return;
    if (endEpochRef.current - Date.now() > 400) return; // transient 0

    // Best-effort tell server; navigation is not blocked on this
    (async () => {
      try {
        const mid = matchIdRef.current;
        if (mid) {
          await axios.post(`${api}/matches/${mid}/end-session`, {}, authHeaders());
          L("POST /matches/:id/end-session");
        } else {
          // legacy hook
          socket.emit("reveal:endStage", { eventId });
          L("emit reveal:endStage (no matchId)");
        }
      } catch {}
      safeNavigateAfterEnd();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateReady, leftSec]);

  /* ---------------- UI ---------------- */
  if (!gateReady) return null; // visual gate (all hooks declared above)

  const h = Math.floor(Math.max(0, leftSec) / 3600);
  const m = Math.floor((Math.max(0, leftSec) % 3600) / 60);
  const s = Math.max(0, leftSec) % 60;

  return (
    <div style={PAGE.shell}>
      <RevealPortalNavBar eventId={eventId} title="Match is Live" />

      <div style={TH.container}>
        <h1 style={TH.title}>
          Match is{" "}
          <span style={TH.highlight}>
            <span style={TH.highlightText}>Live</span>
          </span>
        </h1>

        <div style={TH.subheading}>
          Keep your answers short, honest, and to the point—then pass it back.
        </div>

        <div style={TH.timerWrapper}>
          <div style={TH.timerLabelPill}>Timer Ends In</div>
          <div style={TH.digitsRow} aria-live="polite">
            {h > 0 && (
              <>
                <span style={TH.digit}>{String(h).padStart(2, "0")[0]}</span>
                <span style={TH.digit}>{String(h).padStart(2, "0")[1]}</span>
                <span style={TH.colon}>:</span>
              </>
            )}
            <span style={TH.digit}>{String(m).padStart(2, "0")[0]}</span>
            <span style={TH.digit}>{String(m).padStart(2, "0")[1]}</span>
            <span style={TH.colon}>:</span>
            <span style={TH.digit}>{String(s).padStart(2, "0")[0]}</span>
            <span style={TH.digit}>{String(s).padStart(2, "0")[1]}</span>
          </div>
        </div>
      </div>

      <div style={IC.wrapper}>
        <div style={IC.card}>
          <span style={IC.pill}>Icebreaker</span>
          <h2 style={IC.question}>
  {iceQ || "..."}
</h2>
        </div>
        <p style={IC.footer}>
          When the timer reaches zero we’ll automatically continue. Make the
          most of this chat!
        </p>
      </div>
    </div>
  );
}


/* ---------------- styles ---------------- */
const PAGE = {
  shell: {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    background: "#fff",
  },
};
const TH = {
  container: {
    boxSizing: "border-box",
    maxWidth: 680,
    width: "80%",
    margin: "12px auto 0",
    padding: "0 12px",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    textAlign: "center",
    color: "#0b0b0b",
  },
  title: {
    margin: 0,
    fontSize: "1.85rem",
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: "-0.01em",
  },
  highlight: {
    display: "inline-block",
    padding: "3px 10px",
    paddingBottom: 4,
    borderRadius: 6,
    backgroundColor: "#3579F6",
    color: "#fff",
    transform: "skewX(-8deg) rotate(-2deg)",
  },
  highlightText: {
    display: "inline-block",
    transform: "skewX(8deg) rotate(2deg)",
  },
  subheading: {
    marginTop: 6,
    fontSize: ".9rem",
    color: "#6b7280",
    fontWeight: 500,
    lineHeight: 1.45,
  },
  timerWrapper: {
    marginTop: 18,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  timerLabelPill: {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: 999,
    background: "#fff",
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: "#e5e7eb",
    fontSize: ".7rem",
    color: "#374151",
    fontWeight: 800,
    letterSpacing: ".06em",
    textTransform: "uppercase",
    marginBottom: 8,
  },
  digitsRow: { display: "flex", alignItems: "baseline", gap: 4 },
  digit: {
    display: "inline-block",
    fontSize: "2.5rem",
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.02em",
    color: "#0f172a",
  },
  colon: {
    fontSize: "2rem",
    fontWeight: 900,
    color: "#9ca3af",
    lineHeight: 1,
    padding: "8px 4px",
    transform: "translateY(-5px)",
  },
};
const IC = {
  wrapper: {
    width: "100%",
    maxWidth: 600,
    margin: "0 auto",
    fontFamily:
      "'Open Sans', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    textAlign: "center",
  },
  card: {
    position: "relative",
    padding: "7.5rem 1.5rem 3.25rem",
    borderRadius: 12,
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#ddd",
    boxShadow: "0 4px 50px rgba(165,165,165,0.08)",
    margin: "1rem",
    minHeight: 260,
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",
    background: "#fff",
  },
  pill: {
    position: "absolute",
    top: 80,
    left: "50%",
    transform: "translateX(-50%)",
    color: "#555",
    fontWeight: 700,
    padding: "8px 16px",
    fontSize: ".8rem",
    background: "#eee",
    borderRadius: 100,
  },
  question: {
    fontSize: "1.75rem",
    fontWeight: 550,
    lineHeight: 1.5,
    letterSpacing: "-0.02em",
    margin: "0 auto",
    maxWidth: 360,
    color: "#111",
  },
  footer: {
    fontSize: ".75rem",
    lineHeight: 1.5,
    color: "#9ca3af",
    maxWidth: 300,
    margin: "0.25rem auto 0",
  },
};
